import { Box, Typography } from '@mui/material'
import {
  BarChart,
  DatePicker,
  MButton,
  ProgressLoaders,
  SvgImage,
  YearDropDown,
  YearMonthPicker
} from 'components'
import { MONTHS, RATES_LINK, USER_TOKEN } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context/auth'
import dayjs from 'dayjs'
import { upperFirst } from 'lodash'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import { toTitleCase } from 'utils/helpers/stringUtils'
import { useLocalStorage } from 'utils/hooks'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import * as XLSX from 'xlsx'


export const valueFormatter = (
  value: number | string | null, uom: string
): string => {
  return `${value ?? '0'} ${uom}`
}

const currentYear = new Date().getFullYear()
const lastYear = new Date().getFullYear() - 1

export const renderDateFilter = (
  viewType: 'monthly' | 'daily' | 'hourly',
  value: string,
  onChange: any,
  options?: string[]
): JSX.Element => {
  switch (viewType) {
    case 'monthly':
      return (
        <YearDropDown
          value={value}
          onChange={onChange}
          yearOptions={options ?? [String(currentYear), String(lastYear)]}
        />
      )
    case 'daily':
      return <YearMonthPicker value={value} onChange={onChange} />
    case 'hourly':
      return <DatePicker value={value} onChange={onChange} />
  }
}

const getViewTypeFilterValue = (
  viewType: 'monthly' | 'daily' | 'hourly',
  event: any
): string => {
  switch (viewType) {
    case 'monthly':
      return event.target.value
    case 'daily':
      return event.format('YYYY-MM')
    case 'hourly':
      return event.format('YYYY-MM-DD')
  }
}

interface IUsageViewGraph {
  viewType: 'monthly' | 'daily' | 'hourly'
  filters: Record<string, string>
  setFilters: Dispatch<SetStateAction<Record<string, string>>>
}

export const getUsagePayload = (
  date: string,
  viewType: string
): any => {
  let startDate, endDate
  const frequency = viewType.toUpperCase()[0]
  const dateFilter = date

  if (viewType === 'monthly') {
    startDate = `${dateFilter}-01-01`
    endDate = dayjs(`${dateFilter}-12-31`).format('YYYY-MM-DD')
  } else if (viewType === 'daily') {
    startDate = dayjs(`${dateFilter}-01`).format('YYYY-MM-DD')
    endDate = dayjs(`${dateFilter}-01`).endOf('month').format('YYYY-MM-DD')
  } else if (viewType === 'hourly') {
    startDate = dayjs(date).format('YYYY-MM-DD-HH.mm.ss')
    endDate = dayjs(date).add(1, 'day').format('YYYY-MM-DD-HH.mm.ss')
  }

  return {
    frequency,
    startDate,
    endDate
  }
}


export const UsageViewGraph = ({
  viewType,
  filters,
  setFilters
}: IUsageViewGraph): JSX.Element => {
  const [seriesData, setSeriesData] = useState<number[]>([])
  const [uom, setUom] = useState<string>('')
  const [xAxisData, setXAxisData] = useState<string[]>(MONTHS)
  const [prevAccountId, setPrevAccountId] = useState<string>('')
  const { getUsage, premiseId, currentAccount, account, getAccount, isAdmin, userEmail } =
    useAuth()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const isMobile = useMobileBreakpoints()

  const handleDateFilterChange = (event: any): void => {
    setFilters({
      ...filters,
      [viewType]: getViewTypeFilterValue(viewType, event)
    })
  }

  const handleViewRates = (): void => {
    window.open(RATES_LINK, '_blank')
  }

  const handleExport = (): void => {
    const exportData = xAxisData.map((period, index) => ({
      Period: period,
      Value: seriesData?.[index]
    }))


    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, `${toTitleCase(viewType)} Data - ${filters[viewType]}`)

    XLSX.writeFile(workbook, `${toTitleCase(viewType)} Data - ${filters[viewType]}.xlsx`)
  }


  useEffect(() => {
    if (
      prevAccountId === currentAccount?.accountId ||
      currentAccount?.accountId === account?.myAccount?.accountId
    ) {
      return
    }
    if (currentAccount?.accountId !== null) {
      void getAccount({
        AccessToken: authToken,
        accountId: currentAccount?.accountId,
        admin: isAdmin
      })
    }
    setPrevAccountId(
      currentAccount?.accountId ?? account?.myAccount?.accountId ?? ''
    )
  }, [authToken, currentAccount])

  const fetchUsage = async (frequency: string, startDate: string, endDate: string): Promise<void> => {
    try {
      setIsLoading(true)
      if (premiseId === '') return
      const usageValue = await getUsage({
        AccessToken: authToken,
        premiseId: currentAccount?.premiseId !== '' ? String(currentAccount?.premiseId) : premiseId,
        accountId: currentAccount?.accountId !== '' ? String(currentAccount?.accountId) : account?.myAccount?.accountId,
        frequency,
        startDate,
        endDate,
        email: userEmail,
        service: 'WATER',
        admin: isAdmin
      })

      if (((usageValue?.usage?.usageHistory) != null) && usageValue?.usage?.usageHistory.length !== 0) {
        let inputFormat = ''; let outputFormat = ''

        if (frequency === 'M') {
          inputFormat = 'YYYY-MM'
          outputFormat = 'MMM YYYY'
        } else if (frequency === 'D') {
          inputFormat = 'YYYY-MM-DD'
          outputFormat = 'MMM D'
        } else if (frequency === 'H') {
          inputFormat = 'YYYY-MM-DD-HH'
          outputFormat = 'HH:mm'
        }

        const usageData = usageValue?.usage?.usageHistory?.[0]?.usageData

        const periods = usageData?.map(item => dayjs(item.period, inputFormat).format(outputFormat)) ?? []
        const values = usageData?.map(item => Math.round(Number(item.value))) ?? []

        setXAxisData(periods)
        setSeriesData(values)
        setUom(usageValue?.usage?.usageHistory?.[0]?.uom)
      }
    } catch (error) {
      console.error('usage fetch: ', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (viewType === 'monthly') {
      const { frequency, startDate, endDate } = getUsagePayload(filters.monthly, viewType)
      void fetchUsage(frequency, startDate, endDate)
    } else if (viewType === 'daily') {
      const { frequency, startDate, endDate } = getUsagePayload(filters.daily, viewType)
      void fetchUsage(frequency, startDate, endDate)
    } else if (viewType === 'hourly') {
      const { frequency, startDate, endDate } = getUsagePayload(filters.hourly, viewType)
      void fetchUsage(frequency, startDate, endDate)
    }
  }, [filters, viewType, currentAccount])

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={1}
      >
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="h2" fontSize={24} color={color.grey[900]} fontWeight={500}>
            {`${upperFirst(viewType)} Usage History`}
          </Typography>

          <Typography fontSize={16} color={color.grey[700]} fontWeight={400}>
            {` UOM: ${uom}`}
          </Typography>
        </Box>

        <Box>
          {renderDateFilter(
            viewType,
            filters[viewType],
            handleDateFilterChange
          )}
        </Box>
      </Box>

      {isLoading && <ProgressLoaders height={476} />}

      <BarChart
        seriesData={seriesData}
        xAxisData={xAxisData}
        valueFormatter={(value) => valueFormatter(value, uom)} loading={isLoading} />

      <Box
        display="flex"
        alignItems="center"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={2}
      >
        <MButton
          variant="outlined"
          texttransform="none"
          rounded="true"
          size="large"
          startIcon={<SvgImage name="DownloadIcon" alt="" width={20} height={20} />}
          fullWidth={isMobile}
          onClick={handleExport}
        >
          Export to excel
        </MButton>


        <MButton
          variant="outlined"
          texttransform="none"
          rounded="true"
          size="large"
          startIcon={<SvgImage name="ViewRatesIcon" alt="" width={20} height={20} />}
          onClick={handleViewRates}
          fullWidth={isMobile}
        >
          View Rates
        </MButton>
      </Box>
    </Box>
  )
}
